<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useRoute, useRouter } from 'vue-router';
import Auth from 'dashboard/api/auth';
import Avatar from 'next/avatar/Avatar.vue';
import { useBranding } from 'dashboard/unichat/composables/useBranding';

const store = useStore();
const { accountScopedRoute } = useAccount();
const { logoUrl, logoInternalUrl, fetchBranding } = useBranding();
const navLogoUrl = computed(() => logoInternalUrl.value || logoUrl.value);

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchBranding();
});
const { isAdmin } = useAdmin();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');

// Dropdowns
const settingsOpen = ref(false);
const userMenuOpen = ref(false);
const settingsPos = ref({ top: 0, left: 66 });
const userMenuPos = ref({ bottom: 0, left: 66 });
const settingsBtnRef = ref(null);
const userBtnRef = ref(null);

const toggleSettings = () => {
  if (!settingsOpen.value && settingsBtnRef.value) {
    const rect = settingsBtnRef.value.getBoundingClientRect();
    settingsPos.value = { top: rect.top, left: rect.right + 6 };
  }
  settingsOpen.value = !settingsOpen.value;
  userMenuOpen.value = false;
};

const toggleUserMenu = () => {
  if (!userMenuOpen.value && userBtnRef.value) {
    const rect = userBtnRef.value.getBoundingClientRect();
    userMenuPos.value = { bottom: window.innerHeight - rect.bottom, left: rect.right + 6 };
  }
  userMenuOpen.value = !userMenuOpen.value;
  settingsOpen.value = false;
};

const closeAll = () => {
  settingsOpen.value = false;
  userMenuOpen.value = false;
};

const allNavItems = [
  {
    name: 'conversations',
    label: 'Conversas',
    icon: 'i-lucide-message-circle',
    to: accountScopedRoute('home'),
    match: ['home', 'inbox_conversation', 'conversation_mentions', 'conversation_participating', 'conversation_unattended'],
    adminOnly: false,
  },
  {
    name: 'inbox',
    label: 'Caixa de Entrada',
    icon: 'i-lucide-inbox',
    to: accountScopedRoute('inbox_view'),
    match: ['inbox_view', 'inbox_view_conversation'],
    adminOnly: true,
  },
  {
    name: 'contacts',
    label: 'Contatos',
    icon: 'i-lucide-users',
    to: accountScopedRoute('contacts_dashboard_index'),
    match: ['contacts_dashboard_index', 'contacts_edit'],
    adminOnly: false,
  },
  {
    name: 'reports',
    label: 'Relatórios',
    icon: 'i-lucide-bar-chart-2',
    to: accountScopedRoute('account_overview_reports'),
    match: ['account_overview_reports', 'conversation_reports', 'csat_reports', 'agent_reports_index', 'label_reports_index', 'inbox_reports_index', 'team_reports_index', 'sla_reports'],
    adminOnly: false,
  },
];

const navItems = computed(() =>
  allNavItems.filter(item => !item.adminOnly || isAdmin.value)
);

const settingsItems = computed(() => [
  { label: 'Conta', icon: 'i-lucide-briefcase', route: 'general_settings_index' },
  { label: 'Usuários', icon: 'i-lucide-square-user', route: 'agent_list' },
  { label: 'Usuários Pendentes', icon: 'i-lucide-user-round-x', route: 'unichat_pending_agents' },
  { label: 'Setores / Times', icon: 'i-lucide-users', route: 'settings_teams_list' },
  { label: 'Secretarias', icon: 'i-lucide-building-2', route: 'unichat_sectors_list' },
  { label: 'Aparência da Marca', icon: 'i-lucide-image', route: 'unichat_branding' },
  { label: 'Prefeituras / Caixas de Entrada', icon: 'i-lucide-mailbox', route: 'settings_inbox_list' },
  { label: 'Etiquetas', icon: 'i-lucide-tags', route: 'labels_list' },
  { label: 'Atributos', icon: 'i-lucide-code', route: 'attributes_list' },
  { label: 'Automação', icon: 'i-lucide-repeat', route: 'automation_list' },
  { label: 'Macros', icon: 'i-lucide-toy-brick', route: 'macros_wrapper' },
  { label: 'Respostas Salvas', icon: 'i-lucide-message-square-quote', route: 'canned_list' },
  { label: 'Integrações', icon: 'i-lucide-blocks', route: 'settings_applications' },
  { label: 'SLA', icon: 'i-lucide-clock-alert', route: 'sla_list' },
  { label: 'Segurança', icon: 'i-lucide-shield', route: 'security_settings_index' },
  { label: 'Logs de Auditoria', icon: 'i-lucide-scroll-text', route: 'auditlogs_list' },
]);

const userMenuItems = computed(() => [
  { label: 'Meu Perfil', icon: 'i-lucide-user-pen', route: 'profile_settings_index' },
  { label: 'Aparência', icon: 'i-lucide-palette', route: null, action: 'appearance' },
  { label: 'Atalhos do Teclado', icon: 'i-lucide-keyboard', route: null, action: 'shortcuts' },
  { label: 'Sair', icon: 'i-lucide-power', route: null, action: 'logout', danger: true },
]);

const emit = defineEmits(['openKeyShortcutModal']);

const handleUserAction = item => {
  closeAll();
  if (item.route) {
    router.push(accountScopedRoute(item.route));
  } else if (item.action === 'logout') {
    Auth.logout();
  } else if (item.action === 'shortcuts') {
    emit('openKeyShortcutModal');
  }
};

const goToSettings = item => {
  closeAll();
  router.push(accountScopedRoute(item.route));
};

const isActive = item =>
  item.match.some(r => route.name?.toString().includes(r));

const isSettingsActive = computed(() =>
  ['settings', 'agent_list', 'labels', 'automation', 'canned', 'unichat_', 'sla_list',
   'attributes', 'security', 'macros', 'integration', 'audit'].some(k =>
    route.name?.toString().includes(k)
  )
);
</script>

<template>
  <!-- Overlays para fechar dropdowns -->
  <Teleport to="body">
    <div
      v-if="settingsOpen || userMenuOpen"
      class="fixed inset-0"
      style="z-index:9998;"
      @click="closeAll"
    />

    <!-- Dropdown Configurações -->
    <div
      v-if="settingsOpen"
      class="fixed rounded-xl overflow-hidden"
      :style="`top:${settingsPos.top}px;left:${settingsPos.left}px;z-index:9999;background:#091412;border:1px solid rgba(255,255,255,0.07);box-shadow:0 8px 32px rgba(0,0,0,0.6);min-width:240px;`"
      @click.stop
    >
      <button
        v-for="item in settingsItems"
        :key="item.route"
        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left transition-colors duration-100"
        style="color:rgba(255,255,255,0.75);"
        @click="goToSettings(item)"
        @mouseenter="e => { e.currentTarget.style.background='rgba(18,165,148,0.12)'; e.currentTarget.style.color='#0BD8B6'; }"
        @mouseleave="e => { e.currentTarget.style.background=''; e.currentTarget.style.color='rgba(255,255,255,0.75)'; }"
      >
        <i :class="item.icon" class="size-4 flex-shrink-0 opacity-70" />
        <span>{{ item.label }}</span>
      </button>
    </div>

    <!-- Dropdown Usuário -->
    <div
      v-if="userMenuOpen"
      class="fixed rounded-xl overflow-hidden"
      :style="`bottom:${userMenuPos.bottom}px;left:${userMenuPos.left}px;z-index:9999;background:#091412;border:1px solid rgba(255,255,255,0.07);box-shadow:0 8px 32px rgba(0,0,0,0.6);min-width:200px;`"
      @click.stop
    >
      <div class="px-4 py-3 border-b" style="border-color:rgba(255,255,255,0.07);">
        <div class="text-sm font-semibold" style="color:#F0FDF4;">{{ currentUser?.name }}</div>
        <div class="text-xs mt-0.5" style="color:#4B6B58;">{{ currentUser?.email }}</div>
      </div>
      <button
        v-for="item in userMenuItems"
        :key="item.label"
        class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left transition-colors duration-100"
        :style="item.danger ? 'color:rgba(248,113,113,0.85);' : 'color:rgba(255,255,255,0.75);'"
        @click="handleUserAction(item)"
        @mouseenter="e => { e.currentTarget.style.background='rgba(18,165,148,0.12)'; e.currentTarget.style.color = item.danger ? '#f87171' : '#0BD8B6'; }"
        @mouseleave="e => { e.currentTarget.style.background=''; e.currentTarget.style.color = item.danger ? 'rgba(248,113,113,0.85)' : 'rgba(255,255,255,0.75)'; }"
      >
        <i :class="item.icon" class="size-4 flex-shrink-0 opacity-70" />
        <span>{{ item.label }}</span>
      </button>
    </div>
  </Teleport>

  <!-- Sidebar vertical 58px -->
  <aside
    class="unichat-nav-sidebar flex flex-col items-center flex-shrink-0 py-3 gap-0.5"
    style="width:58px;background:#050B0A;border-right:1px solid rgba(255,255,255,.07);height:100%;z-index:30;overflow:hidden;"
  >
    <!-- Logo -->
    <div class="mb-3 mt-1">
      <img
        :src="navLogoUrl"
        alt="UniChat"
        style="width:30px;height:30px;object-fit:contain;filter:drop-shadow(0 2px 8px rgba(0,0,0,0.5));"
      />
    </div>

    <!-- Itens de navegação -->
    <nav class="flex flex-col items-center gap-0.5 flex-1 w-full">
      <RouterLink
        v-for="item in navItems"
        :key="item.name"
        :to="item.to"
        class="nav-icon-item relative flex items-center justify-center rounded-[11px] cursor-pointer transition-all duration-150"
        style="width:42px;height:42px;color:#4B6B58;"
        :style="isActive(item)
          ? 'background:rgba(18,165,148,.18);color:#0BD8B6;'
          : ''"
        :title="item.label"
      >
        <!-- Indicador ativo (barra esquerda) -->
        <span
          v-if="isActive(item)"
          class="absolute"
          style="left:-8px;top:50%;transform:translateY(-50%);width:3px;height:22px;border-radius:0 3px 3px 0;background:#12A594;"
        />
        <i :class="item.icon" class="size-5" />
      </RouterLink>

      <!-- Configurações (admin only) -->
      <button
        v-if="isAdmin"
        ref="settingsBtnRef"
        class="nav-icon-item relative flex items-center justify-center rounded-[11px] cursor-pointer transition-all duration-150 border-0"
        style="width:42px;height:42px;background:transparent;"
        :style="isSettingsActive || settingsOpen
          ? 'background:rgba(18,165,148,.18);color:#0BD8B6;'
          : 'color:#4B6B58;'"
        title="Configurações"
        @click="toggleSettings"
      >
        <span
          v-if="isSettingsActive"
          class="absolute"
          style="left:-8px;top:50%;transform:translateY(-50%);width:3px;height:22px;border-radius:0 3px 3px 0;background:#12A594;"
        />
        <i class="i-lucide-settings size-5" />
      </button>
    </nav>

    <!-- Avatar do usuário (logout/perfil) -->
    <button
      ref="userBtnRef"
      class="flex items-center justify-center rounded-full border-0 cursor-pointer transition-all duration-150 mb-1"
      style="width:30px;height:30px;background:#12A594;"
      :style="userMenuOpen ? 'opacity:1' : 'opacity:0.85'"
      title="Perfil / Sair"
      @click="toggleUserMenu"
      @mouseenter="e => e.currentTarget.style.opacity = '1'"
      @mouseleave="() => { if (!userMenuOpen) userBtnRef.style && (userBtnRef.style.opacity = '0.85') }"
    >
      <Avatar :name="currentUser?.name" :size="28" hide-offline-status />
    </button>
  </aside>
</template>

<style scoped>
.nav-icon-item:hover {
  background: rgba(255,255,255,.06) !important;
  color: #94A8C4 !important;
}
.nav-icon-item[style*="rgba(34,197,94"]:hover {
  background: rgba(18,165,148,.18) !important;
  color: #0BD8B6 !important;
}
</style>
